import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
    Box,
    Typography,
    Select,
    MenuItem,
} from "@mui/material";
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from "chart.js";
import { useSelector } from "react-redux";
import { getDeviceWiseGraphData } from "../redux/actions/service-action";
import dayjs from "dayjs";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const StorageGraph = () => {
    const { activeCarDetails } = useSelector((state: any) => state.service);
    const [deviceGraphData, setDeviceGraphData] = useState<any>({});
    const [selectedMonth, setSelectedMonth] = useState<any>(null);
    const [storageSummary, setStorageSummary] = useState<any>(null);
    const [netUsage, setNetUsage] = useState<any>(null);

    const getDeviceWiseGraphDataFun = async () => {
        const tunnelUrl = activeCarDetails?.raw?.tunnel_info?.[0];
        if (!tunnelUrl) return;

        try {
            const res: any = await getDeviceWiseGraphData({ tunnelUrl, id: activeCarDetails?.raw?.uuid });
            if (res) {
                setDeviceGraphData(res);
                const months = Object.keys(res).map((date) =>
                    dayjs(date).format("YYYY-MM")
                );
                const uniqueMonths = Array.from(new Set(months)).sort();
                const latestMonth = uniqueMonths[uniqueMonths.length - 1];
                setSelectedMonth(dayjs(latestMonth));
            }
        } catch (error) {
            console.log(error);
        }
    };

    const getStorageSummaryFun = async () => {
        const tunnelUrl = activeCarDetails?.raw?.tunnel_info?.[0];
        if (!tunnelUrl) return;
        try {
            const res = await fetch(
                `${String(tunnelUrl).replace(/\/+$/, "")}/storage-summary`
            );
            if (res.ok) setStorageSummary(await res.json());
        } catch (error) {
            console.log(error);
        }
    };

    // Daily outbound traffic, computed on the device from its own netdata.
    // Boards without the route (or without netdata) answer 404/503, and the
    // section then simply does not render.
    const getNetUsageFun = async () => {
        setNetUsage(null);
        const tunnelUrl = activeCarDetails?.raw?.tunnel_info?.[0];
        if (!tunnelUrl) return;
        try {
            const res = await fetch(
                `${String(tunnelUrl).replace(/\/+$/, "")}/net-usage?days=90`
            );
            if (!res.ok) return;
            const body = await res.json();
            if (body?.days && Object.keys(body.days).length > 0) setNetUsage(body);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (activeCarDetails?.deviceno) {
            getDeviceWiseGraphDataFun();
            getStorageSummaryFun();
            getNetUsageFun();
        }
    }, [activeCarDetails]);

    const availableMonths = Array.from(
        new Set(
            Object.keys(deviceGraphData || {}).map((date) =>
                dayjs(date).format("YYYY-MM")
            )
        )
    ).sort();

    const filteredEntries = Object.entries(deviceGraphData || {})
        .filter(([date]) =>
            selectedMonth
                ? dayjs(date).format("YYYY-MM") === selectedMonth.format("YYYY-MM")
                : false
        )
        .sort(([a], [b]) => dayjs(a).unix() - dayjs(b).unix());

    const labels = filteredEntries.map(([date]) =>
        dayjs(date).format("D")
    );

    const internalData = filteredEntries.map(
        ([, value]: any) => value.internal_gb ?? 0
    );

    const sdCardData = filteredEntries.map(
        ([, value]: any) => value.sd_card_gb ?? 0
    );

    const hasSplit = filteredEntries.some(
        ([, value]: any) => value.internal_gb !== undefined
    );

    const storageData = filteredEntries.map(
        ([, value]: any) => value.disk_gb ?? value.size_gb ?? 0
    );

    const monthTotalGb = storageData.reduce(
        (sum: number, v: number) => sum + (v || 0),
        0
    );

    const allTimeTotalGb = Object.values(deviceGraphData || {}).reduce(
        (sum: number, value: any) => sum + (value?.disk_gb ?? value?.size_gb ?? 0),
        0
    );

    // Data sent over the network, one bar per day of the selected month.
    // The storage figure above is a snapshot of what sits on the disk; this is
    // an amount that left the device each day, so a month total and an
    // overall total are real numbers.
    const sentDays = Object.entries(netUsage?.days || {}) as [string, any][];
    const sentMonth = sentDays
        .filter(([date]) =>
            selectedMonth
                ? dayjs(date).format("YYYY-MM") === selectedMonth.format("YYYY-MM")
                : false
        )
        .sort(([a], [b]) => dayjs(a).unix() - dayjs(b).unix());
    const sentGb = sentMonth.map(([, v]) => (Number(v?.sent_bytes) || 0) / 1e9);
    const monthSentGb = sentGb.reduce((sum: number, v: number) => sum + v, 0);
    const totalSentGb =
        sentDays.reduce((sum: number, [, v]) => sum + (Number(v?.sent_bytes) || 0), 0) / 1e9;
    const sentSince = sentDays.length
        ? dayjs(sentDays.map(([d]) => d).sort()[0]).format("D MMM")
        : "";
    const formatGb = (gb: number) =>
        gb < 1 ? `${(gb * 1000).toFixed(0)} MB` : `${gb.toFixed(2)} GB`;

    const sentChartData = {
        labels: sentMonth.map(([date]) => dayjs(date).format("D")),
        datasets: [
            {
                label: "Data sent",
                data: sentGb,
                // Today is still counting, so it is drawn lighter.
                backgroundColor: sentMonth.map(([, v]) =>
                    v?.partial ? "#f7b79a" : "#ff5b14"
                ),
                borderRadius: 3,
            },
        ],
    };

    const sentOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: (context: any) =>
                        dayjs(sentMonth[context[0].dataIndex][0]).format("DD MMM YYYY"),
                    label: (context: any) => {
                        const partial = sentMonth[context.dataIndex]?.[1]?.partial;
                        return `Sent: ${formatGb(Number(context.raw) || 0)}${
                            partial ? " (so far today)" : ""
                        }`;
                    },
                },
            },
        },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 } } },
            y: {
                grid: { color: "#eee" },
                ticks: { font: { size: 10 } },
                title: { display: true, text: "GB", font: { size: 10 } },
            },
        },
    };

    const data = {
        labels,
        datasets: hasSplit
            ? [
                {
                    label: "Internal",
                    data: internalData,
                    backgroundColor: "#e8cfc3",
                    stack: "stored",
                    borderRadius: 3,
                },
                {
                    label: "SD Card",
                    data: sdCardData,
                    backgroundColor: "#b58b73",
                    stack: "stored",
                    borderRadius: 3,
                },
            ]
            : [
                {
                    label: "Stored Video",
                    data: storageData,
                    backgroundColor: "#e8cfc3",
                    stack: "stored",
                    borderRadius: 3,
                },
            ],
    };

    const options: any = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top",
                align: "start",
                labels: {
                    boxWidth: 14,
                    font: { size: 11 },
                },
            },
            tooltip: {
                callbacks: {
                    title: (context: any) => {
                        const index = context[0].dataIndex;
                        const fullDate = filteredEntries[index][0];
                        return dayjs(fullDate).format("DD MMM YYYY");
                    },
                    label: (context: any) => {
                        const gb = Number(context.raw) || 0;
                        const shown =
                            gb < 1 ? `${(gb * 1024).toFixed(0)} MB` : `${gb.toFixed(2)} GB`;
                        return `${context.dataset.label}: ${shown}`;
                    },
                    footer: (context: any) => {
                        const index = context[0].dataIndex;
                        const total = Number(storageData[index]) || 0;
                        return `Total: ${total.toFixed(2)} GB`;
                    },
                },
            },
        },
        scales: {
            x: {
                stacked: true,
                grid: { display: false },
                ticks: {
                    font: { size: 10 },
                },
            },
            y: {
                stacked: true,
                grid: { color: "#eee" },
                ticks: {
                    font: { size: 10 },
                },
            },
        },
    };

    if (!deviceGraphData || Object.keys(deviceGraphData).length === 0) {
        return <Typography>Loading...</Typography>;
    }

    return (
        <Box sx={{ mt: 1 }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                }}
            >
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    Storage
                </Typography>
                <Select
                    size="small"
                    value={selectedMonth?.format("YYYY-MM") || ""}
                    onChange={(e) => setSelectedMonth(dayjs(e.target.value))}
                    sx={{ fontSize: 12 }}
                >
                    {availableMonths.map((month) => (
                        <MenuItem key={month} value={month}>
                            {dayjs(month).format("MMM YYYY")}
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            <Box sx={{ height: 200 }}>
                <Bar data={data} options={options} />
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                }}
            >
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                    This month: <b>{monthTotalGb.toFixed(2)} GB</b>
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#555" }}>
                    Total stored: <b>{allTimeTotalGb.toFixed(2)} GB</b>
                </Typography>
            </Box>

            {storageSummary && (
                <Box sx={{ mt: 1 }}>
                    {[
                        { key: "internal", label: "Internal" },
                        { key: "sd_card", label: "SD Card" },
                    ].map(({ key, label }) => {
                        const vol = storageSummary[key];
                        if (!vol?.present) return null;
                        return (
                            <Box
                                key={key}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: 11,
                                    color: "#555",
                                }}
                            >
                                <span>{label}</span>
                                <span>
                                    {vol.used_gb} / {vol.total_gb} GB ({vol.used_pct}%)
                                </span>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Typography sx={{ fontSize: 10, color: "#888", mt: 0.5 }}>
                * Unit GB, video currently stored on the device, measured daily.
            </Typography>

            {netUsage && (
                <Box sx={{ mt: 2.5 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                        Data sent
                    </Typography>
                    <Box sx={{ height: 140 }}>
                        <Bar data={sentChartData} options={sentOptions} />
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 1,
                        }}
                    >
                        <Typography sx={{ fontSize: 11, color: "#555" }}>
                            This month: <b>{formatGb(monthSentGb)}</b>
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#555" }}>
                            Total since {sentSince}: <b>{formatGb(totalSentGb)}</b>
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 10, color: "#888", mt: 0.5 }}>
                        * Everything this device sent out over its network connection
                        (uploads, video watched, monitoring), measured per day. Today
                        is still counting.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default StorageGraph;